import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useRequests } from '@/hooks/useRequests';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AgentRequest, RequestStatus } from '@/lib/types';
import { Send, Check, X, MessageSquare, Pencil } from 'lucide-react';
import { format } from 'date-fns';

const MAX_MESSAGE_PREVIEW = 15;

const STATUS_OPTIONS: { value: RequestStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Declined' },
];

function statusBadge(status: RequestStatus) {
  const normalized = status === 'declined' ? 'rejected' : status;
  const styles: Record<string, string> = {
    pending: 'bg-amber-100 text-amber-800 border-amber-200',
    approved: 'bg-green-100 text-green-800 border-green-200',
    rejected: 'bg-red-100 text-red-800 border-red-200',
  };
  const labels: Record<string, string> = {
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Declined',
  };
  return (
    <span
      className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-medium ${styles[normalized] ?? styles.pending}`}
    >
      {labels[normalized] ?? status}
    </span>
  );
}

function messagePreview(message: string, maxLen: number = MAX_MESSAGE_PREVIEW) {
  if (!message) return '—';
  const trimmed = message.trim();
  if (trimmed.length <= maxLen) return trimmed;
  return `${trimmed.slice(0, maxLen)}...`;
}

export function RequestsListPage() {
  const { isTeamLeader } = useAuth();
  const [statusFilter, setStatusFilter] = useState<RequestStatus | 'all'>('all');
  const {
    requests,
    isLoading,
    createRequest,
    updateRequest,
    updateRequestStatus,
  } = useRequests(statusFilter === 'all' ? undefined : statusFilter);

  const [createOpen, setCreateOpen] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState<AgentRequest | null>(null);
  const [editMessage, setEditMessage] = useState('');
  const [detailRequest, setDetailRequest] = useState<AgentRequest | null>(null);

  const handleCreate = () => {
    const msg = newMessage.trim();
    if (!msg) return;
    createRequest.mutate(msg, {
      onSuccess: () => {
        setNewMessage('');
        setCreateOpen(false);
      },
    });
  };

  const openEdit = (req: AgentRequest) => {
    setEditingRequest(req);
    setEditMessage(req.message);
    setEditOpen(true);
  };

  const handleUpdate = () => {
    if (!editingRequest || !editMessage.trim()) return;
    updateRequest.mutate(
      { id: editingRequest.id, message: editMessage.trim() },
      {
        onSuccess: () => {
          setEditOpen(false);
          setEditingRequest(null);
          setEditMessage('');
        },
      }
    );
  };

  const handleApprove = (req: AgentRequest) => {
    updateRequestStatus.mutate({ id: req.id, status: 'approved' });
  };

  const handleDecline = (req: AgentRequest) => {
    updateRequestStatus.mutate({ id: req.id, status: 'rejected' });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Requests</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            {isTeamLeader
              ? 'Review and approve or decline requests from agents in your department.'
              : 'Submit requests to your lead. They can approve or decline.'}
          </p>
        </div>
        {!isTeamLeader && (
          <Button onClick={() => setCreateOpen(true)}>
            <Send className="h-4 w-4 mr-2" />
            New request
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as RequestStatus | 'all')}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            {isTeamLeader ? 'Department requests' : 'My requests'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12 text-muted-foreground">
              Loading requests...
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Message</TableHead>
                  {isTeamLeader && <TableHead>Requester</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted Date</TableHead>
                  <TableHead className="w-[140px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={isTeamLeader ? 5 : 4}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No requests found
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((req) => (
                    <TableRow
                      key={req.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setDetailRequest(req)}
                    >
                      <TableCell className="max-w-[320px]">
                        <p className="text-sm">
                          {messagePreview(req.message)}
                        </p>
                      </TableCell>
                      {isTeamLeader && (
                        <TableCell className="text-sm">
                          {req.createdBy?.profile?.name ??
                            req.createdBy?.email ??
                            '—'}
                        </TableCell>
                      )}
                      <TableCell>{statusBadge(req.status)}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {format(new Date(req.createdAt), 'MMM d, yyyy HH:mm')}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {req.status === 'pending' && (
                            <>
                              {!isTeamLeader && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEdit(req)}
                                  disabled={updateRequest.isPending}
                                  title="Edit message"
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              )}
                              {isTeamLeader && (
                                <>
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => handleApprove(req)}
                                    disabled={updateRequestStatus.isPending}
                                    title="Approve"
                                  >
                                    <Check className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => handleDecline(req)}
                                    disabled={updateRequestStatus.isPending}
                                    title="Decline"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create request dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New request</DialogTitle>
            <DialogDescription>
              Send a request to your department lead. They can approve or decline.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Describe your request..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            rows={4}
            className="resize-none"
            disabled={createRequest.isPending}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!newMessage.trim() || createRequest.isPending}
            >
              {createRequest.isPending ? 'Sending...' : 'Send request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request detail dialog */}
      <Dialog open={!!detailRequest} onOpenChange={(open) => !open && setDetailRequest(null)}>
        <DialogContent className="sm:max-w-xl sm:max-h-xl" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Request details</DialogTitle>
          </DialogHeader>
          {detailRequest && (
            <div className="space-y-4">
              <div className='mt-5'>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Message</p>
                <div className="max-w-full max-h-48 overflow-auto rounded-md bg-muted/50 p-3 border border-border/50">
                  <p className="text-sm whitespace-pre-wrap break-all [overflow-wrap:anywhere]">
                    {detailRequest.message}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className='mt-5'>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Requester</p>
                  <p className="font-medium mt-0.5">
                    {detailRequest.createdBy?.profile?.name ?? detailRequest.createdBy?.email ?? '—'}
                  </p>
                </div>
                <div className='mt-5'>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</p>
                  <div className="mt-0.5">{statusBadge(detailRequest.status)}</div>
                </div>
                <div className='mt-5'>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Created</p>
                  <p className="font-medium mt-0.5">
                    {format(new Date(detailRequest.createdAt), 'MMM d, yyyy HH:mm')}
                  </p>
                </div>
                {detailRequest.updatedAt && (
                  <div className='mt-5'>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Updated</p>
                    <p className="font-medium mt-0.5">
                      {format(new Date(detailRequest.updatedAt), 'MMM d, yyyy HH:mm')}
                    </p>
                  </div>
                )}
              </div>
              {isTeamLeader && detailRequest.status === 'pending' && (
                <DialogFooter >
                  <Button
                    variant="default"
                    onClick={() => {
                      handleApprove(detailRequest);
                      setDetailRequest(null);
                    }}
                    disabled={updateRequestStatus.isPending}
                    className='mt-5'
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Accept
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      handleDecline(detailRequest);
                      setDetailRequest(null);
                    }}
                    disabled={updateRequestStatus.isPending}
                    className='mt-5'
                  >
                    <X className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit request dialog */}
      <Dialog open={editOpen} onOpenChange={(open) => !open && setEditOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit request</DialogTitle>
            <DialogDescription>
              Only pending requests can be edited.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Message"
            value={editMessage}
            onChange={(e) => setEditMessage(e.target.value)}
            rows={4}
            className="resize-none"
            disabled={updateRequest.isPending}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={!editMessage.trim() || updateRequest.isPending}
            >
              {updateRequest.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
